import { useEffect, useState } from "react";
import {
  SHOPIFY_CONFIGURED,
  fetchProducts,
  fetchProductsByCollection,
  fetchCollections,
} from "@/lib/shopify";

/**
 * useShopifyProducts — fetches products from Shopify (or from a collection
 * if `collection` is provided). Returns { products, loading, error }.
 *
 * If Shopify is not configured, returns empty array (UI can fall back to mock data).
 */
export function useShopifyProducts({ first = 12, collection = null, query = "" } = {}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(SHOPIFY_CONFIGURED);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!SHOPIFY_CONFIGURED) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const promise = collection
      ? fetchProductsByCollection(collection, { first })
      : fetchProducts({ first, query });

    promise
      .then((list) => {
        if (cancelled) return;
        setProducts(list.filter(Boolean));
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Failed to load products");
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [first, collection, query]);

  return { products, loading, error };
}

export function useShopifyCollections({ first = 20 } = {}) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(SHOPIFY_CONFIGURED);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!SHOPIFY_CONFIGURED) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchCollections({ first })
      .then((list) => !cancelled && setCollections(list))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [first]);

  return { collections, loading, error };
}
